import { Link } from "react-router-dom";
import { RotateCcw, Clock, CheckCircle, AlertCircle, Mail } from "lucide-react";
import { ParticleBackground } from "@/components/ParticleBackground";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function Returns() {
  return (
    <>
      <SEOHead
        title="Returns Policy | Samboards"
        description="Returns and refund policy for Samboards sensory products. 14-day return window, fair conditions, and simple process."
        canonical="/returns"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Returns", url: "/returns" }
        ]}
      />

      <div className="min-h-screen bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />
        
        <Page>
          <div className="max-w-4xl mx-auto py-8 sm:py-12">
            <header className="text-center mb-10 sm:mb-14">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-300/80 mb-3">
                Returns
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent mb-4">
                Returns & Refunds
              </h1>
              <p className="text-slate-300 max-w-2xl mx-auto">
                We want you to be completely happy with your purchase. If you're not satisfied, here's how returns work.
              </p>
            </header>

            <div className="space-y-8">
              {/* Return Window */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <Clock className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">14-Day Return Window</h2>
                    <p className="text-slate-300 leading-relaxed">
                      You have <strong className="text-emerald-200">14 days</strong> from the date you receive your order 
                      to request a return. This gives you time to inspect the product and ensure it meets your needs.
                    </p>
                  </div>
                </div>
              </section>

              {/* Condition Requirements */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">Condition Requirements</h2>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      To be eligible for a return, items must be:
                    </p>
                    <ul className="space-y-2 text-slate-300">
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-400">✓</span>
                        In original, unused condition
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-400">✓</span>
                        In original packaging (if applicable)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-400">✓</span>
                        Free from damage, marks, or signs of use
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-emerald-400">✓</span>
                        With all accessories and components included
                      </li>
                    </ul>
                    <p className="text-slate-400 text-sm mt-4">
                      We understand that sensory products need to be touched to be assessed. 
                      Light handling to inspect the product is acceptable.
                    </p>
                  </div>
                </div>
              </section>

              {/* Refund Timeline */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <RotateCcw className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">Refund Timeline</h2>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Once we receive and inspect your return, we'll process your refund within 
                      <strong className="text-emerald-200"> 5–7 business days</strong>.
                    </p>
                    <div className="rounded-xl bg-slate-800/50 border border-slate-700/50 p-4 space-y-2">
                      <p className="text-sm text-slate-300">
                        <strong className="text-emerald-200">Step 1:</strong> Return received and inspected (1–2 days)
                      </p>
                      <p className="text-sm text-slate-300">
                        <strong className="text-emerald-200">Step 2:</strong> Refund processed (1–2 days)
                      </p>
                      <p className="text-sm text-slate-300">
                        <strong className="text-emerald-200">Step 3:</strong> Funds appear in your account (3–5 days, depending on bank)
                      </p>
                    </div>
                    <p className="text-slate-400 text-sm mt-4">
                      Refunds are issued to the original payment method. Original shipping costs are non-refundable 
                      unless the return is due to our error.
                    </p>
                  </div>
                </div>
              </section>

              {/* How to Initiate a Return */}
              <section className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-emerald-500/20 p-3 flex-shrink-0">
                    <Mail className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-emerald-100 mb-2">How to Initiate a Return</h2>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      To start a return, please contact us with your order details:
                    </p>
                    <ol className="space-y-3 text-slate-300">
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-sm flex items-center justify-center">1</span>
                        <span>Email us at <a href="mailto:samboards19@aol.com" className="text-emerald-300 hover:text-emerald-200 underline">samboards19@aol.com</a> with "Return Request" in the subject line</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-sm flex items-center justify-center">2</span>
                        <span>Include your order number and the items you wish to return</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-sm flex items-center justify-center">3</span>
                        <span>We'll respond within 1–2 business days with return instructions</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-sm flex items-center justify-center">4</span>
                        <span>Pack items securely and ship to the address provided</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </section>

              {/* Exceptions */}
              <section className="rounded-2xl border border-amber-500/30 bg-amber-900/10 p-6 sm:p-8 backdrop-blur-md">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-amber-500/20 p-3 flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-amber-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-amber-100 mb-2">Non-Returnable Items</h2>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Some items cannot be returned for hygiene or customisation reasons:
                    </p>
                    <ul className="space-y-2 text-slate-300">
                      <li className="flex items-center gap-2">
                        <span className="text-amber-400">•</span>
                        Custom or personalised products
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-amber-400">•</span>
                        Items marked as non-returnable at time of purchase
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-amber-400">•</span>
                        Products that have been heavily used or damaged
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Damaged/Wrong Items */}
              <section className="rounded-2xl border border-emerald-500/30 bg-emerald-900/20 p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-emerald-100 mb-3">Damaged or Incorrect Items?</h2>
                <p className="text-slate-300 mb-4">
                  If your order arrives damaged or you received the wrong items, please contact us immediately. 
                  We'll arrange a replacement or full refund at no extra cost to you.
                </p>
                <p className="text-slate-300 mb-4">
                  Please take photos of any damage and include them in your email to help us resolve the issue quickly.
                </p>
                <p className="text-slate-300">
                  Email:{" "}
                  <a href="mailto:samboards19@aol.com" className="text-emerald-300 hover:text-emerald-200 underline">
                    samboards19@aol.com
                  </a>
                </p>
              </section>

              {/* Quick Links */}
              <div className="flex flex-wrap gap-4 justify-center pt-4">
                <Link 
                  to="/delivery" 
                  className="text-emerald-300 hover:text-emerald-200 underline text-sm"
                >
                  Delivery Information
                </Link>
                <span className="text-slate-600">•</span>
                <Link 
                  to="/contact" 
                  className="text-emerald-300 hover:text-emerald-200 underline text-sm"
                >
                  Contact Us
                </Link>
                <span className="text-slate-600">•</span>
                <Link 
                  to="/faq" 
                  className="text-emerald-300 hover:text-emerald-200 underline text-sm"
                >
                  FAQ
                </Link>
              </div>
            </div>
          </div>
        </Page>
      </div>
    </>
  );
}
